import React, { useState, useEffect } from 'react';
import { Box, useTheme, CircularProgress, Typography, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Divider, Paper, Stack, useMediaQuery, FormControl, InputLabel, Select, MenuItem, Button, Collapse } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetItemQuery, useGetLibraryItemQuery } from '../store/docApi';
import ProjectOverview from './ProjectOverview';
import ProjectWizard from './ProjectWizard';
import Notes from '../Note/Notes';
import NoteDetails from '../Note/NoteDetails';
import Bibliography from '../Reference/Bibliography';
import PictureManager from '../components/widgets/PictureManager';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewSidebarOutlined as ViewSidebarOutlinedIcon,
  ArrowBack as ArrowBackIcon,
  AutoFixHigh as WizardIcon,
  Note as NoteIcon,
  MenuBook as MenuBookIcon,
  List as ListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PlayArrow as InProgressIcon,
  Help as QuestionsIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { PROGRESS_STATE, PROGRESS_KEY } from '../constants/progress';

const WizardStepItem = ({ step, stepId, pattern, progress, onStepClick, level = 0, isExpanded, onExpand }) => {
  const stepProgress = progress?.[PROGRESS_KEY.STEP(stepId.toString())] || PROGRESS_STATE.NOT_STARTED;
  const isCompleted = stepProgress === PROGRESS_STATE.COMPLETED;
  const isInProgress = stepProgress === PROGRESS_STATE.IN_PROGRESS;
  const { type, subId } = useParams();
  const isSelected = type === 'wizard' && subId === stepId;
  const hasSubSteps = step.steps && step.steps.length > 0;

  // Get step title/name from part if available
  const stepTitle = React.useMemo(() => {
    if (step.title || step.name) {
      return step.title || step.name;
    }
    if (step.part_id && pattern?.parts?.[step.part_id]) {
      return pattern.parts[step.part_id].name || pattern.parts[step.part_id].title;
    }
    return `Step ${stepId.toString().split('_').pop()}`;
  }, [step, stepId, pattern]);

  // Check if any child step is selected
  const hasSelectedChild = React.useMemo(() => {
    if (!hasSubSteps || !subId) return false;
    return step.steps.some((_, subIndex) => {
      const childStepId = `${stepId}_${subIndex}`;
      return subId === childStepId;
    });
  }, [hasSubSteps, subId, stepId, step.steps]);

  // Expand if this step is selected or has a selected child
  useEffect(() => {
    if (isSelected || hasSelectedChild) {
      onExpand(stepId, true);
    }
  }, [isSelected, hasSelectedChild, onExpand, stepId]);

  const getProgressIcon = () => {
    if (isCompleted) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    } else if (isInProgress) {
      return <InProgressIcon color="primary" fontSize="small" />;
    } else {
      return <UncheckedIcon color="disabled" fontSize="small" />;
    }
  };

  const getActionIcons = (step) => {
    const actions = [];

    if (step.withInstructions) {
      actions.push(
        <WizardIcon
          key="instructions"
          fontSize="small"
          color="primary"
          sx={{ opacity: 0.7 }}
        />
      );
    }

    if (step.questions) {
      actions.push(
        <QuestionsIcon
          key="questions"
          fontSize="small"
          color="primary"
          sx={{ opacity: 0.7 }}
        />
      );
    }

    return actions.length > 0 ? (
      <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
        {actions}
      </Box>
    ) : null;
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    onExpand(stepId, !isExpanded);
  };

  return (
    <>
      <ListItemButton
        onClick={() => onStepClick(step, stepId.toString())}
        sx={{
          pl: level * 4 + 2,
          bgcolor: isSelected ? 'primary.light' : 'transparent',
          '&:hover': {
            bgcolor: isSelected ? 'primary.light' : 'action.hover'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 40 }}>
          {getProgressIcon()}
        </Box>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {stepTitle}
              </Typography>
              {getActionIcons(step)}
            </Box>
          }
        />
        {hasSubSteps && (
          <IconButton
            onClick={handleExpandClick}
            size="small"
            sx={{
              p: 0.5,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </ListItemButton>
      {hasSubSteps && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {step.steps.map((subStep, subIndex) => (
              <WizardStepItem
                key={`${stepId}_${subIndex}`}
                step={subStep}
                stepId={`${stepId}_${subIndex}`}
                pattern={pattern}
                progress={progress}
                onStepClick={onStepClick}
                level={level + 1}
                isExpanded={isExpanded}
                onExpand={onExpand}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default function Maker() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pk, id, connectionIndex: connectionIndexParam, type, subId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const [isStickyMode, setIsStickyMode] = useState(true);
  const [connectionIndex, setConnectionIndex] = useState(parseInt(connectionIndexParam) || 0);
  const [patternId, setPatternId] = useState();
  const { data: item, error, isLoading } = useGetItemQuery({ label: 'project', pk, id });
  const { data: pattern } = useGetLibraryItemQuery({ id: patternId }, { skip: !patternId });
  const [expandedWizard, setExpandedWizard] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  // Function to get all parent step IDs for a given step ID
  const getParentStepIds = React.useCallback((stepId) => {
    const parts = stepId.split('_');
    const parentIds = [];
    for (let i = 1; i < parts.length; i++) {
      parentIds.push(parts.slice(0, i).join('_'));
    }
    return parentIds;
  }, []);

  const handleStepExpand = React.useCallback((stepId, isExpanded) => {
    setExpandedSteps((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(stepId);
      } else {
        newSet.delete(stepId);
      }
      return newSet;
    });
  }, []);

  // Update expanded steps when subId changes
  useEffect(() => {
    if (type === 'wizard' && subId) {
      const parentIds = getParentStepIds(subId);
      setExpandedSteps(new Set(parentIds));
    }
  }, [type, subId, getParentStepIds]);

  // Update connection index and pattern ID when params change
  useEffect(() => {
    if (item) {
      const newConnectionIndex = parseInt(connectionIndexParam) || 0;
      if (newConnectionIndex !== connectionIndex) {
        setConnectionIndex(newConnectionIndex);
      }
      setPatternId(item.connections && item.connections.length > 0 ? item.connections[newConnectionIndex]._id : undefined);
    }
  }, [item, connectionIndexParam, connectionIndex]);

  useEffect(() => {
    if (type === 'wizard' && subId) {
      setExpandedWizard(true);
    }
  }, [type, subId]);

  const handleConnectionChange = (newIndex) => {
    setConnectionIndex(newIndex);
    setPatternId(item.connections[newIndex]._id);
    navigate(`/projects/${item.pk}/${item.id}/${newIndex}/${type || ''}${subId ? `/${subId}` : ''}`);
  };

  const handleViewPattern = () => {
    navigate(`/library/${item.connections[connectionIndex]._id}`);
  };

  const getPatternName = (connection, index) => {
    return connection._name || `Pattern ${index + 1}`;
  };

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleViewModeToggle = () => {
    setIsStickyMode(!isStickyMode);
    if (!isStickyMode) {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  };

  const getSections = () => {
    return [
      { id: '', icon: <ListIcon />, title: 'Overview', description: 'Summary and progress' },
      { id: 'wizard', icon: <WizardIcon />, title: 'Wizard', description: 'Interactive step-by-step guide' },
      { id: 'notes', icon: <NoteIcon />, title: 'Notes', description: 'Notes and comments' },
      { id: 'pictures', icon: <ImageIcon />, title: 'Pictures', description: 'Progress photo collection' },
      { id: 'bibliography', icon: <MenuBookIcon />, title: 'Bibliography', description: 'References and resources' },
    ];
  };

  const handleNavigate = (sectionId, stepId = '') => {
    navigate(`/projects/${item.pk}/${item.id}/${connectionIndex}/${sectionId}${stepId ? `/${stepId}` : ''}`);
  };

  const handleWizardStepClick = (stepId) => {
    navigate(`/projects/${item.pk}/${item.id}/${connectionIndex}/wizard/${stepId}`);
  };

  const renderMasterList = () => {
    const sections = getSections();

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            height: 48,
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <IconButton onClick={() => navigate('/projects')} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'text.primary'
                  }
                }}
                onClick={() => navigate('/projects')}
              >
                Back to projects
              </Typography>
            </Box>
            {!isMobile && (
              <IconButton onClick={handleViewModeToggle} size="small">
                {isStickyMode ? <ViewSidebarOutlinedIcon /> : <ViewSidebarIcon />}
              </IconButton>
            )}
            {isMobile && (
              <IconButton size="small" onClick={handleDrawerToggle}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Box>

        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel id="connection-select-label">Select Pattern</InputLabel>
            <Select
              labelId="connection-select-label"
              id="connection-select"
              label="Select Pattern"
              value={connectionIndex}
              onChange={(e) => handleConnectionChange(e.target.value)}
            >
              {item.connections?.map((connection, i) => (
                <MenuItem key={i} value={i}>
                  {getPatternName(connection, i)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={handleViewPattern}
          >
            View in Library
          </Button>
        </Box>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {sections.map((section) => (
            <React.Fragment key={section.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (section.id === 'wizard') {
                      setExpandedWizard(!expandedWizard);
                    } else if (section.id === 'notes') {
                      setExpandedNotes(!expandedNotes);
                    } else {
                      handleNavigate(section.id);
                      if (!isStickyMode) {
                        setIsDrawerOpen(false);
                      }
                    }
                  }}
                  selected={type === section.id}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {section.icon}
                        <Typography variant="subtitle2">{section.title}</Typography>
                      </Box>
                    }
                    secondary={section.description}
                  />
                  {section.id === 'wizard' && (
                    expandedWizard ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  )}
                  {section.id === 'notes' && (
                    expandedNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  )}
                </ListItemButton>
              </ListItem>
              {section.id === 'wizard' && (
                <Collapse in={expandedWizard} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {pattern?.steps?.map((step, index) => (
                      <WizardStepItem
                        key={index}
                        step={step}
                        stepId={index.toString()}
                        pattern={pattern}
                        progress={item?.connections?.[connectionIndex]?.progress}
                        onStepClick={(step, stepId) => {
                          handleWizardStepClick(stepId);
                          if (!isStickyMode) {
                            setIsDrawerOpen(false);
                          }
                        }}
                        isExpanded={expandedSteps.has(index.toString())}
                        onExpand={handleStepExpand}
                      />
                    ))}
                  </List>
                </Collapse>
              )}
              {section.id === 'notes' && expandedNotes && (
                <Collapse in={expandedNotes} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item?.connections?.[connectionIndex]?.notes?.map((note, index) => (
                      <ListItemButton
                        key={note.id || index}
                        onClick={() => {
                          handleNavigate('notes', note.id);
                          if (!isStickyMode) {
                            setIsDrawerOpen(false);
                          }
                        }}
                        selected={type === 'notes' && subId === note.id}
                        sx={{ pl: 4 }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {note.name || 'Untitled Note'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" noWrap>
                              {note.text?.substring(0, 30) || ''}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Paper>
    );
  };

  const renderHeader = () => {
    if (!item) return null;

    return (
      <Box
        sx={{
          height: 48,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(isMobile || !isStickyMode) && (
            <IconButton onClick={handleDrawerToggle} size="small">
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="subtitle1"
            onClick={() => navigate(`/projects/${item.pk}/${item.id}`)}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {item.name}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (!item) return null;

    switch (type) {
      case 'wizard':
        return (
          <ProjectWizard
            item={item}
            stepIndex={subId ? parseInt(subId) : 0}
            stepId={subId}
            connectionIndex={connectionIndex}
            pattern={pattern}
            onStepChange={(stepId) => {
              handleWizardStepClick(stepId);
            }}
          />
        );
      case 'notes':
        if (subId) {
          return (
            <NoteDetails
              item={item}
              noteid={subId}
              connectionIndex={connectionIndex}
            />
          );
        }
        return (
          <Notes
            project={item}
            connectionIndex={connectionIndex}
            onNoteSelect={(noteId) => {
              handleNavigate('notes', noteId);
            }}
          />
        );
      case 'bibliography':
        return <Bibliography />;
      case 'pictures':
        return <PictureManager item={item} />;
      default:
        return (
          <ProjectOverview
            pattern={pattern}
            currentStepId={subId}
            item={item}
            connectionIndex={connectionIndex}
            onStepSelect={(stepId) => {
              handleNavigate('overview', stepId);
            }}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.palette.background.default
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !item) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.palette.background.default
        }}
      >
        <Typography color="error">
          {error ? `Error loading project: ${error.message}` : 'Project not found'}
        </Typography>
      </Box>
    );
  }

  const drawer = <Box sx={{ width: 300, height: '100%' }}>{renderMasterList()}</Box>;

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        bgcolor: 'background.default'
      }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={isDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              height: '100%',
              position: 'relative'
            }
          }}
        >
          {drawer}
        </Drawer>
      ) : isStickyMode ? (
        <Box sx={{ width: 300, flexShrink: 0 }}>{drawer}</Box>
      ) : (
        <Drawer
          variant="temporary"
          anchor="left"
          open={isDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 300,
              boxSizing: 'border-box',
              height: '100vh',
              position: 'fixed',
              top: 0,
              borderRadius: 0
            }
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {renderHeader()}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}